/**
 * Starter Section — a content block introduced by an uppercase eyebrow
 * label, with an optional leading icon (tinted with the brand
 * `--color-primary`) and an optional right-aligned meta string.
 *
 * The header is purely visual — Section doesn't wrap content in a card.
 * Compose with Card / SimpleGrid / Stack / etc. for the inner layout.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import styles from './Section.module.css';

export interface SectionProps extends HTMLAttributes<HTMLDivElement> {
  /** Uppercase eyebrow label (e.g. "MORNING BRIEFING"). */
  readonly label: ReactNode;
  /** Optional leading icon (16px, brand-tinted via `--color-primary`). */
  readonly icon?: ReactNode;
  /** Right-aligned meta text ("Today · 5 items"). */
  readonly meta?: ReactNode;
  readonly children?: ReactNode;
}

export const Section = forwardRef<HTMLDivElement, SectionProps>(
  function Section({ label, icon, meta, className, children, ...rest }, ref) {
    return (
      <section
        ref={ref}
        className={[styles.root, className].filter(Boolean).join(' ')}
        {...rest}
      >
        <header className={styles.header}>
          <div className={styles.left}>
            {icon !== undefined ? (
              <span className={styles.icon}>{icon}</span>
            ) : null}
            <span className={styles.label}>{label}</span>
          </div>
          {meta !== undefined ? <span className={styles.meta}>{meta}</span> : null}
        </header>
        {children}
      </section>
    );
  },
);
